import type { ReactNode } from "react";
import { cloneElement, isValidElement, useEffect, useId } from "react";
import { FLAG_LABELS, type PunchFlag } from "../../shared/policy";
import type { PunchResult, VisitStatus } from "../../shared/types";
import type { ApiError } from "../lib/api";

export function Spinner({ label = "Loading" }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

export function Loading() {
  return (
    <div className="center">
      <Spinner />
    </div>
  );
}

export function ErrorBanner({ error, onRetry }: { error: ApiError | Error | null; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <div className="banner danger" role="alert">
      <div className="row between">
        <span>{error.message}</span>
        {onRetry && (
          <button className="btn sm" onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

export function Banner({ kind, children }: { kind: "ok" | "warn" | "danger" | "info"; children: ReactNode }) {
  return (
    <div className={`banner ${kind}`} role={kind === "danger" ? "alert" : "status"}>
      {children}
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  const hintId = useId();
  // Link the hint with aria-describedby so screen readers don't read it as part of the label.
  const control =
    hint && isValidElement<{ "aria-describedby"?: string }>(children)
      ? cloneElement(children, { "aria-describedby": hintId })
      : children;
  return (
    <div className="field">
      <label>
        <span>{label}</span>
        {control}
      </label>
      {hint && (
        <div className="hint" id={hintId}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function FlagBadges({ flags }: { flags: PunchFlag[] }) {
  if (!flags.length) return null;
  return (
    <span className="row" style={{ gap: 4 }}>
      {flags.map((f) => (
        <span key={f} className={`badge ${f === "offline_sync" || f === "not_assigned" ? "info" : "warn"}`}>
          {FLAG_LABELS[f] ?? f}
        </span>
      ))}
    </span>
  );
}

const RESULT_BADGE: Record<PunchResult, [string, string]> = {
  ok: ["ok", "OK"],
  flagged: ["warn", "Flagged"],
  pending: ["info", "Needs approval"],
  approved: ["ok", "Approved"],
  rejected: ["danger", "Rejected"],
};

export function ResultBadge({ result }: { result: PunchResult }) {
  const [kind, label] = RESULT_BADGE[result];
  return <span className={`badge ${kind}`}>{label}</span>;
}

const STATUS_BADGE: Record<VisitStatus, [string, string]> = {
  open: ["info", "On site"],
  closed: ["", "Closed"],
  missing_clock_out: ["danger", "Missing clock-out"],
  rejected: ["danger", "Rejected"],
};

export function StatusBadge({ status }: { status: VisitStatus }) {
  const [kind, label] = STATUS_BADGE[status];
  return <span className={`badge ${kind}`}>{label}</span>;
}

export function Dialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click closes; Escape is handled above
    <div className="dialog-backdrop" onClick={onClose} role="presentation">
      <div
        className="dialog stack"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="row between">
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted">{children}</p>;
}
